/**
 * Legacy savings accept page for tenants with useLegacyOperationUrls (e.g. Rakbank).
 * URL: /assets/[assetId]/buy/savings?savingsPlanID=... (matches partner ACCEPT regex).
 * Mounted from apps/rakbank/.../(legacy-freeze)/buy/savings/page.tsx.
 * Fetches savings plan via getCreatedSavingsPlanAction and renders AcceptSavingsPlanController.
 */

import { notFound, redirect } from 'next/navigation';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { isDevelopmentEnv } from '@bts-web/utils-functions';
import { AssetDetailsControllerWithSuspense } from '../../../asset-details';
import { AssetDetailTradeWrapper, GenericErrorMessage } from '../../../layouts';
import {
  getCreatedSavingsPlanAction,
  AcceptSavingsPlanController,
} from '../../AcceptSavingsPlan';
import { TradeControllerWithSuspense } from '../../CreateTrade/MainTradeController/TradeControllerWithSuspense';

interface LegacyFreezeAcceptSavingsPageProps {
  params: Promise<{ assetId?: string }>;
  searchParams?: Promise<Record<string, string | undefined>>;
}

export async function LegacyFreezeAcceptSavingsPage({
  params,
  searchParams,
}: LegacyFreezeAcceptSavingsPageProps) {
  const { assetId } = await params;

  if (assetId == null) {
    notFound();
  }

  const search = searchParams != null ? await searchParams : {};

  const normalizedSearch = Object.fromEntries(
    Object.entries(search).filter(
      (entry): entry is [string, string] => entry[1] != undefined,
    ),
  );

  const savingsPlanId = search?.savingsPlanID ?? search?.savingsPlanId;

  if (!savingsPlanId) {
    return (
      <AssetDetailTradeWrapper
        hideMobile="assetElement"
        AssetDetailsContent={
          <AssetDetailsControllerWithSuspense assetId={assetId} />
        }
        TradeStateContent={
          <TradeControllerWithSuspense
            routeParams={{ assetId, operation: 'savings' }}
            searchParams={normalizedSearch}
          />
        }
      />
    );
  }

  const savingsPlanPreAcceptResponse = await getCreatedSavingsPlanAction({
    savingsPlanId,
  });

  // Matches non-legacy savings accept (AcceptOfferIndexPage): redirect to asset details on GraphQL errors in production. Errors are already logged by gqlNetworkFetch before the action returns.
  if (!isDevelopmentEnv()) {
    if ((savingsPlanPreAcceptResponse?.errors?.length || 0) > 0) {
      redirect(NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId));
    }
  }

  const savingsPlan = savingsPlanPreAcceptResponse?.data?.preAcceptSavingsPlan;

  if (!savingsPlan) {
    return <GenericErrorMessage />;
  }

  return (
    <AssetDetailTradeWrapper
      startFocusValue={true}
      hideMobile="assetElement"
      AssetDetailsContent={
        <AssetDetailsControllerWithSuspense assetId={assetId} />
      }
      TradeStateContent={
        <AcceptSavingsPlanController savingsPlanData={savingsPlan} />
      }
    />
  );
}
